/**
 * Notification Management Types
 * Shared types for the Super Admin «Avisos» screen: broadcasts and their delivery log
 */

/**
 * Notification channels available for delivery.
 *
 * BUG-77 — `NotificationChannel` is intentionally an OPEN enum (alias to
 * `string`) because the canonical list lives in `bat.notification_channels`
 * (DB catalog). The enum literals below are the canonical seed values and
 * convenience constants; runtime validation goes through the backend
 * `channelRegistry` (`isKnownChannel`) which checks membership against the
 * DB-driven snapshot.
 *
 * Adding a new channel = `INSERT INTO bat.notification_channels` (no code
 * change required for the contract).
 */
export const NotificationChannel = {
  EMAIL: 'email',
  PUSH: 'push',
  IN_APP: 'in_app',
  SMS: 'sms',
} as const
export type NotificationChannel = string

/**
 * The channels a BROADCAST is actually delivered through today.
 *
 * ⚠️ e2e 2026-09-24: a broadcast wrote rows into `bat.notifications` with status
 * `pending` that nothing ever read or sent, on every channel. `in_app` is now
 * delivered into `bat.user_notifications` (the customer's bell); email, push
 * and SMS have no sender behind a broadcast, so they are refused instead of
 * reported as sent.
 */
export const DELIVERED_BROADCAST_CHANNELS: readonly string[] = ['in_app']

/**
 * Broadcast status values
 */
export enum BroadcastStatus {
  DRAFT = 'draft',
  SCHEDULED = 'scheduled',
  SENDING = 'sending',
  COMPLETED = 'completed',
  COMPLETED_WITH_ERRORS = 'completed_with_errors',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
}

/**
 * Notification Broadcast
 */
export interface NotificationBroadcast {
  id: string
  name: string
  subject: string | null
  body: string | null
  channels: NotificationChannel[]
  targetCriteria: Record<string, unknown>
  status: BroadcastStatus
  scheduledFor: string | null
  totalRecipients: number
  sentCount: number
  failedCount: number
  skippedCount: number
  startedAt: string | null
  completedAt: string | null
  errorMessage: string | null
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Create Notification Broadcast Request
 */
export interface CreateNotificationBroadcastRequest {
  name: string
  subject?: string
  body?: string
  channels?: NotificationChannel[]
  targetCriteria?: Record<string, unknown>
  scheduledFor?: string
}

/**
 * Update Notification Broadcast Request
 */
export interface UpdateNotificationBroadcastRequest {
  name?: string
  subject?: string
  body?: string
  channels?: NotificationChannel[]
  targetCriteria?: Record<string, unknown>
  scheduledFor?: string
}

/**
 * Paginated list response
 */
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

/**
 * Broadcast list response
 */
export interface NotificationBroadcastListResponse
  extends PaginatedResponse<NotificationBroadcast> {}

/**
 * Send broadcast response
 */
export interface SendBroadcastResponse {
  broadcastId: string
  status: BroadcastStatus
  message: string
  estimatedRecipients?: number
  /** Everyone the broadcast was addressed to: `sentCount + failedCount + skippedCount`. */
  totalRecipients: number
  /** Delivered to the bell. */
  sentCount: number
  /** The delivery threw (the rest were still delivered). */
  failedCount: number
  /** Not delivered because the person turned off «Actualizaciones del Sistema» (or the `info` type) in their profile. */
  skippedCount: number
}

/**
 * Delivery log entry
 */
export interface NotificationDeliveryLogEntry {
  id: string
  tenantId: string
  tenantName?: string
  userId: string | null
  userEmail?: string
  templateId: string | null
  templateName?: string
  channel: NotificationChannel
  status: 'sent' | 'failed' | 'pending' | 'cancelled'
  errorMessage: string | null
  sentAt: string | null
  createdAt: string
}

/**
 * Delivery log list response
 */
export interface NotificationDeliveryLogListResponse
  extends PaginatedResponse<NotificationDeliveryLogEntry> {}

/**
 * Delivery log filters
 */
export interface NotificationDeliveryLogFilters {
  tenantId?: string
  channel?: NotificationChannel
  status?: string
  startDate?: string
  endDate?: string
  search?: string
}
